import {
  IonBadge,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import {
  arrowBackOutline,
  arrowForwardOutline,
  buildOutline,
  cameraOutline,
  checkmarkCircleOutline,
  paperPlaneOutline,
  qrCodeOutline,
  refreshOutline,
  searchOutline,
  swapHorizontalOutline,
} from "ionicons/icons";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeCanvas } from "qrcode.react";

import {
  aceptarTransferencia,
  aceptarTransferenciaDirecta,
  buscarFuncionarios,
  cancelarTransferencia,
  FuncionarioActivo,
  generarQRTransferencia,
  MiTemaItem,
  obtenerMiSaldo,
  obtenerMisTemas,
  obtenerMovimientosStock,
  obtenerStockBuckets,
  obtenerTransferenciasPendientes,
  previewTransferencia,
  rechazarTransferencia,
  registrarConsumo,
  solicitarTransferencia,
  StockBucket,
  StockMovimiento,
  StockTransferenciaPreviewResponse,
  StockTransferenciaQRResponse,
  Tema,
  TransferenciaPendiente,
} from "src/services/entregasMasivas";

type Modo =
  | "menu"
  | "elegir_transferencia"
  | "transferir"
  | "recibir"
  | "solicitar"
  | "pendientes"
  | "consumo";

const obtenerUbicacion = (): Promise<{
  lat: number | null;
  lng: number | null;
  precision: number | null;
}> =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: null, lng: null, precision: null });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          precision: pos.coords.accuracy ?? null,
        }),
      () => resolve({ lat: null, lng: null, precision: null }),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });

const bucketLabel = (b: StockBucket): string => {
  const cat = b.categoria || "General";
  return b.identificador ? `${cat} - N° ${b.identificador}` : cat;
};

const SCANNER_ID = "trx-scanner-region";

// Etiqueta legible para mostrar el tipo de cada movimiento.
const tipoLabel: Record<StockMovimiento["tipo"], string> = {
  ingreso: "Ingreso",
  transferencia: "Transferencia",
  entrega: "Entrega",
  reverso_entrega: "Reverso (anulada)",
  consumo: "Consumo / instalación",
  reverso_consumo: "Reverso de consumo",
};

const MiStock: React.FC = () => {
  const [modo, setModo] = useState<Modo>("menu");

  const [temas, setTemas] = useState<MiTemaItem[]>([]);
  const [tema, setTema] = useState<Tema | null>(null);
  const [cargandoTema, setCargandoTema] = useState(true);

  const [saldo, setSaldo] = useState<number>(0);
  const [cargandoSaldo, setCargandoSaldo] = useState(true);

  // Transferencia sin QR (solicitud dirigida)
  const [buckets, setBuckets] = useState<StockBucket[]>([]);
  const [solBusqueda, setSolBusqueda] = useState("");
  const [solResultados, setSolResultados] = useState<FuncionarioActivo[]>([]);
  const [solBuscando, setSolBuscando] = useState(false);
  const [solDestino, setSolDestino] = useState<FuncionarioActivo | null>(null);
  const [solCantidad, setSolCantidad] = useState<string>("");
  const [solMotivo, setSolMotivo] = useState<string>("");
  const [solBuckets, setSolBuckets] = useState<StockBucket[]>([]);
  const [solBucketSearch, setSolBucketSearch] = useState("");
  const [enviandoSol, setEnviandoSol] = useState(false);

  // Consumo / instalacion (el punto de servicio se hereda del ingreso del item)
  const [conSel, setConSel] = useState<StockBucket[]>([]);
  const [conBucketSearch, setConBucketSearch] = useState("");
  const [conMotivo, setConMotivo] = useState("");
  const [enviandoConsumo, setEnviandoConsumo] = useState(false);

  // Recepciones / solicitudes pendientes
  const [pendRecibidas, setPendRecibidas] = useState<TransferenciaPendiente[]>([]);
  const [pendEnviadas, setPendEnviadas] = useState<TransferenciaPendiente[]>([]);
  const [cargandoPend, setCargandoPend] = useState(false);
  const [procesandoPend, setProcesandoPend] = useState<number | null>(null);

  const [movimientos, setMovimientos] = useState<StockMovimiento[]>([]);
  const [cargandoMov, setCargandoMov] = useState(false);

  // Transferir
  const [cantidad, setCantidad] = useState<string>("");
  const [generandoQR, setGenerandoQR] = useState(false);
  const [qrData, setQrData] = useState<StockTransferenciaQRResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const tickRef = useRef<number | null>(null);

  // Recibir
  const [estadoCamara, setEstadoCamara] = useState<
    "iniciando" | "escaneando" | "preview" | "aplicando" | "ok" | "error"
  >("iniciando");
  const [errorCamara, setErrorCamara] = useState<string>("");
  const [preview, setPreview] = useState<StockTransferenciaPreviewResponse | null>(null);
  const [qrPendiente, setQrPendiente] = useState<string>("");
  const [motivoRecepcion, setMotivoRecepcion] = useState<string>("");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const procesandoRef = useRef<boolean>(false);

  const [present] = useIonToast();

  // ---------------------------------------------------------------------------
  // Carga inicial
  // ---------------------------------------------------------------------------
  useEffect(() => {
    obtenerMisTemas()
      .then((lista) => {
        const operables = lista.filter((t) => t.es_admin || t.es_entregador);
        setTemas(operables);
        setTema(operables[0] || null);
      })
      .catch(() => {
        setTemas([]);
        setTema(null);
      })
      .finally(() => setCargandoTema(false));
    return () => {
      detenerScanner();
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar el tema seleccionado recargamos todo lo dependiente.
  useEffect(() => {
    if (!tema) return;
    cargarSaldoYMovimientos();
    cargarPendientes();
    if (tema.controla_categorias) {
      obtenerStockBuckets(tema.id).then(setBuckets).catch(() => setBuckets([]));
    } else {
      setBuckets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tema?.id]);

  const cargarSaldoYMovimientos = async () => {
    const temaId = tema?.id;
    setCargandoSaldo(true);
    setCargandoMov(true);
    try {
      const s = await obtenerMiSaldo(temaId);
      setSaldo(s.saldo || 0);
    } catch {
      setSaldo(0);
    } finally {
      setCargandoSaldo(false);
    }
    try {
      const m = await obtenerMovimientosStock({ tema_id: temaId, limite: 30 });
      setMovimientos(m.datos || []);
    } catch {
      setMovimientos([]);
    } finally {
      setCargandoMov(false);
    }
  };

  const cargarPendientes = async () => {
    if (!tema) return;
    setCargandoPend(true);
    try {
      const r = await obtenerTransferenciasPendientes(tema.id);
      setPendRecibidas(r.recibidas || []);
      setPendEnviadas(r.enviadas || []);
    } catch {
      setPendRecibidas([]);
      setPendEnviadas([]);
    } finally {
      setCargandoPend(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Transferir (genera QR para que el receptor escanee)
  // ---------------------------------------------------------------------------
  const irATransferir = () => {
    setModo("transferir");
    setCantidad("");
    setQrData(null);
  };

  const generarQR = async () => {
    const n = parseInt(cantidad, 10);
    if (!n || n <= 0) {
      present({ message: "Cantidad invalida", duration: 2500, color: "warning" });
      return;
    }
    if (n > saldo) {
      present({
        message: `Solo tenes ${saldo} kits disponibles`,
        duration: 3000,
        color: "warning",
      });
      return;
    }
    setGenerandoQR(true);
    try {
      const resp = await generarQRTransferencia(n);
      setQrData(resp);
      setSecondsLeft(resp.ttl_seconds || 300);
      if (tickRef.current) window.clearInterval(tickRef.current);
      tickRef.current = window.setInterval(() => {
        setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo generar el QR",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setGenerandoQR(false);
    }
  };

  const finalizarTransferencia = async () => {
    if (tickRef.current) window.clearInterval(tickRef.current);
    setQrData(null);
    setCantidad("");
    setModo("menu");
    await cargarSaldoYMovimientos();
  };

  // ---------------------------------------------------------------------------
  // Recibir (escanea QR de otra persona)
  // ---------------------------------------------------------------------------
  const detenerScanner = async () => {
    try {
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        // @ts-ignore html5-qrcode expone getState() en runtime
        if (s.getState && s.getState() === 2) {
          await s.stop();
        }
        s.clear();
      }
    } catch {
      /* no-op */
    }
  };

  const irARecibir = () => {
    setModo("recibir");
    setPreview(null);
    setQrPendiente("");
    setMotivoRecepcion("");
    setErrorCamara("");
    setEstadoCamara("iniciando");
    setTimeout(() => iniciarCamara(), 100);
  };

  const iniciarCamara = async () => {
    setErrorCamara("");
    setEstadoCamara("iniciando");
    try {
      await detenerScanner();
      const scanner = new Html5Qrcode(SCANNER_ID, false);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => onScan(decodedText),
        () => {
          /* errores de frame ignorados */
        }
      );
      setEstadoCamara("escaneando");
    } catch (e: any) {
      setEstadoCamara("error");
      setErrorCamara(
        typeof e === "string"
          ? e
          : e?.message || "No se pudo iniciar la camara. Verifica permisos."
      );
    }
  };

  const onScan = async (qr: string) => {
    if (procesandoRef.current) return;
    procesandoRef.current = true;
    try {
      const data = await previewTransferencia(qr);
      setPreview(data);
      setQrPendiente(qr);
      setEstadoCamara("preview");
      await detenerScanner();
    } catch (e: any) {
      const msg = e?.response?.data?.error || "QR de transferencia invalido";
      present({ message: msg, duration: 3000, color: "danger" });
    } finally {
      setTimeout(() => {
        procesandoRef.current = false;
      }, 1500);
    }
  };

  const aceptar = async () => {
    // Guard contra doble-click: el state cambia de forma asincrona en React,
    // asi que un click rapido podria entrar dos veces aca antes de que
    // estadoCamara se actualice.
    if (!qrPendiente || procesandoRef.current) return;
    procesandoRef.current = true;
    setEstadoCamara("aplicando");
    try {
      const resp = await aceptarTransferencia(qrPendiente, motivoRecepcion || undefined);
      setEstadoCamara("ok");
      setSaldo(resp.saldo);
      present({
        message: `Recibiste ${resp.cantidad} kits de ${
          resp.emisor.nombre || resp.emisor.cedula
        }`,
        duration: 3500,
        color: "success",
      });
    } catch (e: any) {
      setEstadoCamara("error");
      setErrorCamara(e?.response?.data?.error || "No se pudo aplicar la transferencia");
    } finally {
      procesandoRef.current = false;
    }
  };

  const rechazar = () => {
    setPreview(null);
    setQrPendiente("");
    setMotivoRecepcion("");
    setEstadoCamara("iniciando");
    // Diferimos el inicio para que el div del scanner ya este visible.
    setTimeout(() => {
      iniciarCamara();
    }, 100);
  };

  const finalizarRecepcion = async () => {
    setPreview(null);
    setQrPendiente("");
    setMotivoRecepcion("");
    setModo("menu");
    await detenerScanner();
    await cargarSaldoYMovimientos();
  };

  // ---------------------------------------------------------------------------
  // Transferir sin QR (solicitud dirigida que el receptor acepta)
  // ---------------------------------------------------------------------------
  const irASolicitar = () => {
    setModo("solicitar");
    setSolBusqueda("");
    setSolResultados([]);
    setSolDestino(null);
    setSolCantidad("");
    setSolMotivo("");
    setSolBuckets([]);
    setSolBucketSearch("");
  };

  const bucketKey = (b: StockBucket) =>
    `${b.categoria || ""}|${b.identificador || ""}`;

  const toggleSolBucket = (b: StockBucket) => {
    setSolBuckets((prev) =>
      prev.some((x) => bucketKey(x) === bucketKey(b))
        ? prev.filter((x) => bucketKey(x) !== bucketKey(b))
        : [...prev, b]
    );
  };

  const buscarSol = async () => {
    const q = solBusqueda.trim();
    if (!q) {
      setSolResultados([]);
      return;
    }
    setSolBuscando(true);
    try {
      const data = await buscarFuncionarios(q);
      setSolResultados(data.slice(0, 30));
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "Error al buscar",
        duration: 3000,
        color: "danger",
      });
    } finally {
      setSolBuscando(false);
    }
  };

  const enviarSolicitud = async () => {
    if (!tema || !solDestino) return;
    const destinoNombre = solDestino.nombre || solDestino.cedula;
    const motivo = solMotivo.trim() || undefined;

    // En temas con categorias se transfiere un item por cada serie elegida
    // (una solicitud por item). En temas generales se usa la cantidad.
    if (tema.controla_categorias) {
      if (solBuckets.length === 0) {
        present({ message: "Elegi al menos un item a transferir", duration: 2500, color: "warning" });
        return;
      }
      setEnviandoSol(true);
      let ok = 0;
      const errores: string[] = [];
      for (const b of solBuckets) {
        try {
          await solicitarTransferencia({
            tema_id: tema.id,
            cedula_destino: solDestino.cedula,
            cantidad: 1,
            categoria: b.categoria ?? undefined,
            identificador: b.identificador ?? undefined,
            motivo,
          });
          ok++;
        } catch (e: any) {
          errores.push(`${bucketLabel(b)}: ${e?.response?.data?.error || "error"}`);
        }
      }
      setEnviandoSol(false);
      if (ok > 0) {
        present({
          message:
            errores.length === 0
              ? `${ok} solicitud(es) enviada(s) a ${destinoNombre}`
              : `${ok} enviada(s), ${errores.length} con error`,
          duration: 3500,
          color: errores.length === 0 ? "success" : "warning",
        });
        setModo("menu");
        await cargarPendientes();
      } else {
        present({ message: errores[0] || "No se pudo enviar", duration: 3500, color: "danger" });
      }
      return;
    }

    // Tema general: cantidad fungible.
    const n = parseInt(solCantidad, 10);
    if (!n || n <= 0) {
      present({ message: "Cantidad invalida", duration: 2500, color: "warning" });
      return;
    }
    setEnviandoSol(true);
    try {
      await solicitarTransferencia({
        tema_id: tema.id,
        cedula_destino: solDestino.cedula,
        cantidad: n,
        motivo,
      });
      present({
        message: `Solicitud enviada a ${destinoNombre}`,
        duration: 3000,
        color: "success",
      });
      setModo("menu");
      await cargarPendientes();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo enviar la solicitud",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setEnviandoSol(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Consumo / instalacion en un punto de servicio
  // ---------------------------------------------------------------------------
  const irAConsumo = () => {
    setModo("consumo");
    setConSel([]);
    setConBucketSearch("");
    setConMotivo("");
  };

  const toggleConBucket = (b: StockBucket) => {
    setConSel((prev) =>
      prev.some((x) => bucketKey(x) === bucketKey(b))
        ? prev.filter((x) => bucketKey(x) !== bucketKey(b))
        : [...prev, b]
    );
  };

  // Todos los items seleccionados deben tener punto de servicio (heredado del
  // ingreso). Si alguno no lo tiene, no se puede registrar el consumo.
  const conSelSinPs = conSel.filter((b) => !b.punto_servicio && !b.punto_servicio_nombre);

  const enviarConsumo = async () => {
    if (!tema || conSel.length === 0 || conSelSinPs.length > 0) return;
    setEnviandoConsumo(true);
    try {
      const ubic = await obtenerUbicacion();
      let ok = 0;
      const errores: string[] = [];
      let ultimoSaldo: number | undefined;
      for (const b of conSel) {
        try {
          // El PS lo hereda el backend del ingreso del item; lo enviamos igual
          // para dejar la traza consistente con lo mostrado.
          const resp = await registrarConsumo({
            tema_id: tema.id,
            categoria: b.categoria ?? undefined,
            identificador: b.identificador ?? undefined,
            cantidad: 1,
            punto_servicio: b.punto_servicio ?? undefined,
            punto_servicio_nombre: b.punto_servicio_nombre ?? undefined,
            lat: ubic.lat,
            lng: ubic.lng,
            precision: ubic.precision,
            motivo: conMotivo.trim() || undefined,
          });
          ok++;
          if (typeof resp.saldo === "number") ultimoSaldo = resp.saldo;
        } catch (e: any) {
          errores.push(`${bucketLabel(b)}: ${e?.response?.data?.error || "error"}`);
        }
      }
      if (typeof ultimoSaldo === "number") setSaldo(ultimoSaldo);
      setEnviandoConsumo(false);
      const accion = tema.etiqueta_consumo || "Consumo";
      if (ok > 0) {
        present({
          message:
            errores.length === 0
              ? `${accion}: ${ok} registrado(s)`
              : `${ok} ok, ${errores.length} con error`,
          duration: 3500,
          color: errores.length === 0 ? "success" : "warning",
        });
        setModo("menu");
        if (tema.controla_categorias) {
          obtenerStockBuckets(tema.id).then(setBuckets).catch(() => {});
        }
        await cargarSaldoYMovimientos();
      } else {
        present({ message: errores[0] || "No se pudo registrar", duration: 3500, color: "danger" });
      }
    } catch {
      setEnviandoConsumo(false);
      present({ message: "No se pudo registrar", duration: 3500, color: "danger" });
    }
  };

  const aceptarPendiente = async (t: TransferenciaPendiente) => {
    setProcesandoPend(t.id);
    try {
      const resp = await aceptarTransferenciaDirecta(t.id);
      setSaldo(resp.saldo);
      present({
        message: `Recibiste ${t.cantidad} de ${t.nombre_origen || t.cedula_origen}`,
        duration: 3000,
        color: "success",
      });
      await cargarPendientes();
      await cargarSaldoYMovimientos();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo aceptar",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setProcesandoPend(null);
    }
  };

  const rechazarPendiente = async (t: TransferenciaPendiente) => {
    setProcesandoPend(t.id);
    try {
      await rechazarTransferencia(t.id);
      present({ message: "Solicitud rechazada", duration: 2500, color: "medium" });
      await cargarPendientes();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo rechazar",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setProcesandoPend(null);
    }
  };

  const cancelarPendiente = async (t: TransferenciaPendiente) => {
    setProcesandoPend(t.id);
    try {
      await cancelarTransferencia(t.id);
      present({ message: "Solicitud cancelada", duration: 2500, color: "medium" });
      await cargarPendientes();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo cancelar",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setProcesandoPend(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const titulo = tema?.nombre ? `Mi stock - ${tema.nombre}` : "Mi stock";

  const totalPendientes = pendRecibidas.length;

  const renderMenu = () => (
    <>
      {temas.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Tema</div>
          <IonSelect
            value={tema?.id}
            interface="action-sheet"
            onIonChange={(e) => {
              const elegido = temas.find((t) => t.id === e.detail.value);
              if (elegido) setTema(elegido);
            }}
            style={{ border: "1px solid #ccc", borderRadius: 8, paddingLeft: 12 }}
          >
            {temas.map((t) => (
              <IonSelectOption key={t.id} value={t.id}>
                {t.nombre}
              </IonSelectOption>
            ))}
          </IonSelect>
        </div>
      )}

      <div
        style={{
          textAlign: "center",
          padding: "32px 16px",
          background: "linear-gradient(135deg,#910434,#9c1d48)",
          color: "#fff",
          borderRadius: 16,
          margin: "8px 0 16px",
        }}
      >
        <div style={{ fontSize: 14, opacity: 0.85 }}>Saldo disponible</div>
        <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1.1 }}>
          {cargandoSaldo ? "..." : saldo}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
          {tema?.descripcion || "kits"}
        </div>
      </div>

      {tema?.modo_salida === "consumo" && (
        <IonButton
          expand="block"
          color="primary"
          onClick={irAConsumo}
          disabled={saldo <= 0}
          style={{ marginBottom: 12 }}
        >
          <IonIcon slot="start" icon={buildOutline} />
          Registrar {tema?.etiqueta_consumo || "consumo"}
        </IonButton>
      )}

      <IonButton
        expand="block"
        fill={tema?.modo_salida === "consumo" ? "outline" : "solid"}
        onClick={() => setModo("elegir_transferencia")}
        disabled={saldo <= 0}
        style={{ marginBottom: 12 }}
      >
        <IonIcon slot="start" icon={arrowForwardOutline} />
        Transferir
      </IonButton>

      <IonButton
        expand="block"
        color="medium"
        onClick={irARecibir}
        style={{ marginBottom: 12 }}
      >
        <IonIcon slot="start" icon={cameraOutline} />
        Recibir transferencia (QR)
      </IonButton>

      <IonButton
        expand="block"
        fill="outline"
        color="primary"
        onClick={() => {
          setModo("pendientes");
          cargarPendientes();
        }}
        style={{ marginBottom: 24 }}
      >
        <IonIcon slot="start" icon={swapHorizontalOutline} />
        Recepciones pendientes
        {totalPendientes > 0 && (
          <IonBadge color="danger" style={{ marginLeft: 8 }}>
            {totalPendientes}
          </IonBadge>
        )}
      </IonButton>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Movimientos recientes</h3>
        <IonButton fill="clear" size="small" onClick={cargarSaldoYMovimientos}>
          <IonIcon slot="icon-only" icon={refreshOutline} />
        </IonButton>
      </div>

      {cargandoMov && <IonSpinner name="crescent" />}
      {!cargandoMov && movimientos.length === 0 && (
        <p style={{ color: "#777" }}>Aun no hay movimientos.</p>
      )}
      {!cargandoMov && movimientos.length > 0 && (
        <IonList>
          {movimientos.map((m) => (
            <IonItem key={m.id} lines="full">
              <IonLabel className="ion-text-wrap">
                <h3 style={{ margin: 0 }}>
                  {tipoLabel[m.tipo] || m.tipo} ({m.cantidad})
                </h3>
                <p style={{ margin: "4px 0", color: "#555" }}>
                  {m.cedula_origen ? (
                    <>
                      <b>{m.nombre_origen || m.cedula_origen}</b>
                      {" -> "}
                    </>
                  ) : (
                    <>
                      <b>(deposito)</b>
                      {" -> "}
                    </>
                  )}
                  {m.cedula_destino ? (
                    <b>{m.nombre_destino || m.cedula_destino}</b>
                  ) : m.cedula_receptor ? (
                    <b>{m.nombre_receptor || m.cedula_receptor}</b>
                  ) : (
                    <b>(colaborador)</b>
                  )}
                </p>
                {(m.categoria || m.identificador) && (
                  <p style={{ margin: 0, fontSize: 12, color: "var(--garnet, #910434)" }}>
                    {m.categoria || "-"}
                    {m.identificador ? ` (N° ${m.identificador})` : ""}
                  </p>
                )}
                {(m.punto_servicio_nombre || m.punto_servicio) && (
                  <p style={{ margin: 0, fontSize: 12, color: "#00695c" }}>
                    📍 {m.punto_servicio_nombre || m.punto_servicio}
                  </p>
                )}
                {m.motivo && (
                  <p style={{ margin: 0, fontSize: 12, color: "#777" }}>
                    {m.motivo}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                  {m.fecha_hora ? new Date(m.fecha_hora).toLocaleString() : ""}
                </p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      )}
    </>
  );

  const renderTransferir = () => (
    <>
      <IonButton fill="clear" onClick={() => setModo("menu")}>
        <IonIcon slot="start" icon={arrowBackOutline} /> Volver
      </IonButton>

      {!qrData && (
        <div style={{ padding: "8px 0" }}>
          <h2>Transferir</h2>
          <p style={{ color: "#666" }}>
            Saldo disponible: <b>{saldo}</b>
          </p>
          <IonItem>
            <IonLabel position="stacked">Cantidad a transferir</IonLabel>
            <IonInput
              type="number"
              inputmode="numeric"
              min="1"
              max={String(saldo)}
              value={cantidad}
              onIonChange={(e) => setCantidad(e.detail.value || "")}
              placeholder="Ej: 60"
            />
          </IonItem>
          <IonButton
            expand="block"
            style={{ marginTop: 16 }}
            onClick={generarQR}
            disabled={generandoQR}
          >
            {generandoQR ? "Generando..." : "Generar QR"}
          </IonButton>
          <p style={{ fontSize: 12, color: "#777", marginTop: 8 }}>
            Despues de generarlo, mostrale el QR a la otra persona para que lo
            escanee desde su app. La transferencia se aplica recien cuando el
            receptor confirma.
          </p>
        </div>
      )}

      {qrData && (
        <div style={{ textAlign: "center", padding: 8 }}>
          <h2 style={{ marginTop: 0 }}>QR de transferencia</h2>
          <p style={{ color: "#444" }}>
            Vas a transferir <b>{qrData.cantidad}</b> kits.
          </p>
          <p style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
            La otra persona debe escanear este codigo desde su pantalla
            "Recibir transferencia" y confirmar.
          </p>

          <div
            style={{
              display: "inline-block",
              padding: 16,
              background: "#fff",
              border: "2px solid #910434",
              borderRadius: 12,
              marginTop: 8,
            }}
          >
            <QRCodeCanvas value={qrData.qr} size={260} level="M" includeMargin={false} />
            <div style={{ marginTop: 6, color: "#910434", fontWeight: 700, fontSize: 12 }}>
              TRANSFERENCIA
            </div>
          </div>

          <div style={{ marginTop: 16, color: "#666" }}>
            Expira en {Math.floor(secondsLeft / 60)}:
            {String(secondsLeft % 60).padStart(2, "0")}
          </div>

          <IonButton expand="block" style={{ marginTop: 16 }} onClick={finalizarTransferencia}>
            Listo
          </IonButton>
        </div>
      )}
    </>
  );

  const renderRecibir = () => (
    <>
      <IonButton fill="clear" onClick={finalizarRecepcion}>
        <IonIcon slot="start" icon={arrowBackOutline} /> Volver
      </IonButton>

      {estadoCamara === "preview" && preview && (
        <div style={{ textAlign: "center", padding: 16 }}>
          <IonIcon
            icon={qrCodeOutline}
            style={{ fontSize: 80, color: "#910434" }}
          />
          <h2>Confirmar transferencia</h2>
          <p style={{ fontSize: 18, marginBottom: 4 }}>
            <b>{preview.nombre_emisor || preview.cedula_emisor}</b>
          </p>
          <p style={{ color: "#444", marginTop: 0 }}>
            te quiere transferir <b>{preview.cantidad}</b> kits.
          </p>

          <IonItem style={{ marginTop: 16 }}>
            <IonLabel position="stacked">Motivo (opcional)</IonLabel>
            <IonTextarea
              rows={2}
              value={motivoRecepcion}
              onIonChange={(e) => setMotivoRecepcion(e.detail.value || "")}
              placeholder="Ej: refuerzo del punto X"
            />
          </IonItem>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <IonButton expand="block" color="medium" onClick={rechazar} style={{ flex: 1 }}>
              Cancelar
            </IonButton>
            <IonButton expand="block" color="primary" onClick={aceptar} style={{ flex: 1 }}>
              Aceptar
            </IonButton>
          </div>
        </div>
      )}

      {estadoCamara === "aplicando" && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <IonSpinner name="crescent" />
          <p>Aplicando transferencia...</p>
        </div>
      )}

      {estadoCamara === "ok" && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <IonIcon
            icon={checkmarkCircleOutline}
            style={{ fontSize: 96, color: "#2e7d32" }}
          />
          <h2>Transferencia recibida</h2>
          <p style={{ color: "#444" }}>
            Tu nuevo saldo es <b>{saldo}</b> kits.
          </p>
          <IonButton expand="block" onClick={finalizarRecepcion}>
            Listo
          </IonButton>
        </div>
      )}

      {estadoCamara === "error" && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <p style={{ color: "#c62828" }}>{errorCamara || "Error al procesar"}</p>
          <IonButton onClick={irARecibir}>Reintentar</IonButton>
        </div>
      )}

      <div
        id={SCANNER_ID}
        style={{
          width: "100%",
          maxWidth: 360,
          margin: "8px auto 0",
          display:
            estadoCamara === "escaneando" || estadoCamara === "iniciando"
              ? "block"
              : "none",
        }}
      />

      {estadoCamara === "escaneando" && (
        <p style={{ textAlign: "center", color: "#555", marginTop: 8 }}>
          Apunta la camara al QR de transferencia.
        </p>
      )}
    </>
  );

  const renderSolicitar = () => (
    <>
      <IonButton fill="clear" onClick={() => setModo("menu")}>
        <IonIcon slot="start" icon={arrowBackOutline} /> Volver
      </IonButton>

      <h2 style={{ marginTop: 0 }}>Transferir sin QR</h2>
      <p style={{ color: "#666", marginTop: -6 }}>
        Elegi a quien transferir. La persona recibira la solicitud y la acepta
        desde "Recepciones pendientes". El saldo se descuenta al aceptar.
      </p>

      {!solDestino && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <IonInput
              placeholder="Buscar por cedula o nombre"
              value={solBusqueda}
              onIonChange={(e) => setSolBusqueda(e.detail.value || "")}
              onKeyUp={(e) => {
                if ((e as any).key === "Enter") buscarSol();
              }}
              style={{ border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 }}
            />
            <IonButton onClick={buscarSol} disabled={solBuscando}>
              <IonIcon slot="icon-only" icon={searchOutline} />
            </IonButton>
          </div>
          {solBuscando && <IonSpinner name="dots" />}
          <IonList>
            {solResultados.map((f) => (
              <IonItem key={f.cedula} button onClick={() => setSolDestino(f)}>
                <IonLabel>
                  <h4>{f.nombre}</h4>
                  <p>
                    {f.cedula} - {f.cargo}
                  </p>
                </IonLabel>
                <IonButton slot="end">Elegir</IonButton>
              </IonItem>
            ))}
          </IonList>
        </>
      )}

      {solDestino && (
        <>
          <IonItem lines="full">
            <IonLabel>
              <h4>{solDestino.nombre}</h4>
              <p>
                {solDestino.cedula} - {solDestino.cargo}
              </p>
            </IonLabel>
            <IonButton slot="end" fill="clear" onClick={() => setSolDestino(null)}>
              Cambiar
            </IonButton>
          </IonItem>

          {tema?.controla_categorias && (
            <div style={{ margin: "12px 0" }}>
              <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
                Items a transferir{" "}
                {solBuckets.length > 0 && (
                  <b style={{ color: "var(--garnet, #910434)" }}>
                    ({solBuckets.length} seleccionado(s))
                  </b>
                )}
              </div>
              <IonItem>
                <IonIcon icon={searchOutline} slot="start" />
                <IonInput
                  placeholder="Buscar por categoria o N° de serie"
                  value={solBucketSearch}
                  onIonChange={(e) => setSolBucketSearch(e.detail.value || "")}
                />
              </IonItem>
              {buckets.length === 0 && (
                <p style={{ color: "#999", fontSize: 13, margin: 0 }}>
                  No tenes items con saldo en este tema.
                </p>
              )}
              <IonList style={{ maxHeight: 240, overflowY: "auto" }}>
                {buckets
                  .filter((b) => {
                    const q = solBucketSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      (b.categoria || "").toLowerCase().includes(q) ||
                      (b.identificador || "").toLowerCase().includes(q)
                    );
                  })
                  .map((b) => {
                    const checked = solBuckets.some(
                      (x) => bucketKey(x) === bucketKey(b)
                    );
                    return (
                      <IonItem key={bucketKey(b)} button onClick={() => toggleSolBucket(b)}>
                        <IonCheckbox slot="start" checked={checked} />
                        <IonLabel className="ion-text-wrap">
                          {bucketLabel(b)}
                          <span style={{ color: "#888" }}> · {b.saldo}</span>
                        </IonLabel>
                      </IonItem>
                    );
                  })}
              </IonList>
            </div>
          )}

          {!tema?.controla_categorias && (
            <IonItem>
              <IonLabel position="stacked">Cantidad</IonLabel>
              <IonInput
                type="number"
                inputmode="numeric"
                min="1"
                value={solCantidad}
                onIonChange={(e) => setSolCantidad(e.detail.value || "")}
                placeholder="Ej: 10"
              />
            </IonItem>
          )}
          <IonItem>
            <IonLabel position="stacked">Motivo (opcional)</IonLabel>
            <IonTextarea
              rows={2}
              value={solMotivo}
              onIonChange={(e) => setSolMotivo(e.detail.value || "")}
              placeholder="Ej: refuerzo del punto X"
            />
          </IonItem>
          <IonButton
            expand="block"
            style={{ marginTop: 16 }}
            onClick={enviarSolicitud}
            disabled={enviandoSol}
          >
            {enviandoSol ? "Enviando..." : "Enviar solicitud"}
          </IonButton>
        </>
      )}
    </>
  );

  const renderItemPendiente = (
    t: TransferenciaPendiente,
    acciones: React.ReactNode
  ) => (
    <IonItem key={t.id} lines="full">
      <IonLabel className="ion-text-wrap">
        <h3 style={{ margin: 0 }}>{t.cantidad} unidad(es)</h3>
        {(t.categoria || t.identificador) && (
          <p style={{ margin: "2px 0", color: "var(--garnet, #910434)" }}>
            {t.categoria || "-"}
            {t.identificador ? ` (N° ${t.identificador})` : ""}
          </p>
        )}
        {(t.punto_servicio_nombre || t.punto_servicio) && (
          <p style={{ margin: "2px 0", fontSize: 12, color: "#00695c" }}>
            📍 {t.punto_servicio_nombre || t.punto_servicio}
          </p>
        )}
        <p style={{ margin: "2px 0", color: "#555" }}>
          <b>{t.nombre_origen || t.cedula_origen}</b>
          {" -> "}
          <b>{t.nombre_destino || t.cedula_destino}</b>
        </p>
        {t.motivo && (
          <p style={{ margin: 0, fontSize: 12, color: "#777" }}>{t.motivo}</p>
        )}
        <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
          {t.creado_en ? new Date(t.creado_en).toLocaleString() : ""}
        </p>
        <div style={{ marginTop: 8 }}>{acciones}</div>
      </IonLabel>
    </IonItem>
  );

  const renderPendientes = () => (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <IonButton fill="clear" onClick={() => setModo("menu")}>
          <IonIcon slot="start" icon={arrowBackOutline} /> Volver
        </IonButton>
        <IonButton fill="clear" size="small" onClick={cargarPendientes}>
          <IonIcon slot="icon-only" icon={refreshOutline} />
        </IonButton>
      </div>

      <h2 style={{ marginTop: 0 }}>Recepciones pendientes</h2>
      {cargandoPend && <IonSpinner name="crescent" />}

      <h3>Para aceptar</h3>
      {!cargandoPend && pendRecibidas.length === 0 && (
        <p style={{ color: "#777" }}>No tenes transferencias por aceptar.</p>
      )}
      <IonList>
        {pendRecibidas.map((t) =>
          renderItemPendiente(
            t,
            <div style={{ display: "flex", gap: 8 }}>
              <IonButton
                size="small"
                color="medium"
                disabled={procesandoPend === t.id}
                onClick={() => rechazarPendiente(t)}
              >
                Rechazar
              </IonButton>
              <IonButton
                size="small"
                color="primary"
                disabled={procesandoPend === t.id}
                onClick={() => aceptarPendiente(t)}
              >
                Aceptar
              </IonButton>
            </div>
          )
        )}
      </IonList>

      <h3 style={{ marginTop: 16 }}>Enviadas (esperando aceptacion)</h3>
      {!cargandoPend && pendEnviadas.length === 0 && (
        <p style={{ color: "#777" }}>No tenes solicitudes enviadas pendientes.</p>
      )}
      <IonList>
        {pendEnviadas.map((t) =>
          renderItemPendiente(
            t,
            <IonButton
              size="small"
              color="danger"
              fill="outline"
              disabled={procesandoPend === t.id}
              onClick={() => cancelarPendiente(t)}
            >
              Cancelar
            </IonButton>
          )
        )}
      </IonList>
    </>
  );

  const renderElegirTransferencia = () => (
    <>
      <IonButton fill="clear" onClick={() => setModo("menu")}>
        <IonIcon slot="start" icon={arrowBackOutline} /> Volver
      </IonButton>

      <h2 style={{ marginTop: 0 }}>¿Cómo querés transferir?</h2>
      <p style={{ color: "#666", marginTop: -6 }}>
        Saldo disponible: <b>{saldo}</b>
      </p>

      <IonButton
        expand="block"
        onClick={irATransferir}
        style={{ marginTop: 12, marginBottom: 12 }}
      >
        <IonIcon slot="start" icon={qrCodeOutline} />
        Con QR
      </IonButton>
      <p style={{ color: "#888", fontSize: 13, marginTop: -6 }}>
        Generás un QR y la otra persona lo escanea y confirma en el momento.
      </p>

      <IonButton
        expand="block"
        fill="outline"
        onClick={irASolicitar}
        style={{ marginTop: 12, marginBottom: 12 }}
      >
        <IonIcon slot="start" icon={paperPlaneOutline} />
        Sin QR
      </IonButton>
      <p style={{ color: "#888", fontSize: 13, marginTop: -6 }}>
        Enviás la solicitud a una persona y la acepta desde "Recepciones
        pendientes" cuando pueda.
      </p>
    </>
  );

  const renderConsumo = () => (
    <>
      <IonButton fill="clear" onClick={() => setModo("menu")}>
        <IonIcon slot="start" icon={arrowBackOutline} /> Volver
      </IonButton>

      <h2 style={{ marginTop: 0 }}>
        Registrar {tema?.etiqueta_consumo || "consumo"}
      </h2>
      <p style={{ color: "#666", marginTop: -6 }}>
        Elegí el/los item(s) de tu stock. El punto de servicio ya viene definido
        desde el ingreso del item. Salen de tu stock y queda registrado el
        movimiento.
      </p>

      <div style={{ margin: "12px 0" }}>
        <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
          Items{" "}
          {conSel.length > 0 && (
            <b style={{ color: "var(--garnet, #910434)" }}>
              ({conSel.length} seleccionado(s))
            </b>
          )}
        </div>
        <IonItem>
          <IonIcon icon={searchOutline} slot="start" />
          <IonInput
            placeholder="Buscar por categoria o N° de serie"
            value={conBucketSearch}
            onIonChange={(e) => setConBucketSearch(e.detail.value || "")}
          />
        </IonItem>
        {buckets.length === 0 && (
          <p style={{ color: "#999", fontSize: 13 }}>
            No tenes items con saldo en este tema.
          </p>
        )}
        <IonList style={{ maxHeight: 300, overflowY: "auto" }}>
          {buckets
            .filter((b) => {
              const q = conBucketSearch.trim().toLowerCase();
              if (!q) return true;
              return (
                (b.categoria || "").toLowerCase().includes(q) ||
                (b.identificador || "").toLowerCase().includes(q)
              );
            })
            .map((b) => {
              const checked = conSel.some((x) => bucketKey(x) === bucketKey(b));
              const ps = b.punto_servicio_nombre || b.punto_servicio;
              return (
                <IonItem key={bucketKey(b)} button onClick={() => toggleConBucket(b)}>
                  <IonCheckbox slot="start" checked={checked} />
                  <IonLabel className="ion-text-wrap">
                    <div>
                      {bucketLabel(b)}
                      <span style={{ color: "#888" }}> · {b.saldo}</span>
                    </div>
                    {ps ? (
                      <div style={{ fontSize: 12, color: "#00695c" }}>📍 {ps}</div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#c62828" }}>
                        ⚠ Sin punto de servicio asignado
                      </div>
                    )}
                  </IonLabel>
                </IonItem>
              );
            })}
        </IonList>
      </div>

      {conSelSinPs.length > 0 && (
        <p style={{ color: "#c62828", fontSize: 13 }}>
          Hay items seleccionados sin punto de servicio asignado. El admin debe
          asignarlo en el ingreso del stock.
        </p>
      )}

      <IonItem>
        <IonLabel position="stacked">Nota (opcional)</IonLabel>
        <IonTextarea
          rows={2}
          value={conMotivo}
          onIonChange={(e) => setConMotivo(e.detail.value || "")}
          placeholder="Ej: instalado en acceso principal"
        />
      </IonItem>

      <IonButton
        expand="block"
        style={{ marginTop: 16 }}
        onClick={enviarConsumo}
        disabled={enviandoConsumo || conSel.length === 0 || conSelSinPs.length > 0}
      >
        {enviandoConsumo
          ? "Registrando..."
          : `Registrar ${tema?.etiqueta_consumo || "consumo"}`}
      </IonButton>
    </>
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>{titulo}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {cargandoTema && (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        )}
        {!cargandoTema && !tema && (
          <p style={{ textAlign: "center", color: "#777" }}>
            No hay un tema activo de entregas masivas.
          </p>
        )}
        {!cargandoTema && tema && modo === "menu" && renderMenu()}
        {!cargandoTema && tema && modo === "elegir_transferencia" && renderElegirTransferencia()}
        {!cargandoTema && tema && modo === "transferir" && renderTransferir()}
        {!cargandoTema && tema && modo === "recibir" && renderRecibir()}
        {!cargandoTema && tema && modo === "solicitar" && renderSolicitar()}
        {!cargandoTema && tema && modo === "pendientes" && renderPendientes()}
        {!cargandoTema && tema && modo === "consumo" && renderConsumo()}
      </IonContent>
    </IonPage>
  );
};

export default MiStock;
